import { t } from "i18next";
import { useMemo } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import { showErrorToast } from "#app/notifications";
import { CreateMaterialLayoutDto } from "#modules/materialLayouts/infra/createMaterialLayout.api";
import { useCreateMaterialLayout } from "#modules/materialLayouts/view/useCreateMaterialLayout";
import { useUpdateMaterialLayout } from "#modules/materialLayouts/view/useUpdateMaterialLayout";
import type { CreateMaterialDto } from "#modules/materials/infra/createMaterial.api";
import { useCreateMaterial } from "#modules/materials/view/useCreateMaterial";
import { useUpdateMaterial } from "#modules/materials/view/useUpdateMaterial";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { QuoteRequestLineOverthicknessDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLineOverthickness.api";
import { RawMaterialDimensionsForm } from "#modules/quoteRequestLines/view/RawMaterialDimensionsForm";
import { MaterialRawPrice } from "#modules/quoteRequestLines/views/DetailsPanel/Material/MaterialRawPrice";
import { PartHeader } from "#modules/quoteRequestLines/views/QuoteRequestLine/PartHeader";
import { useAnalytics } from "#shared/useAnalytics";
import { CheckboxController } from "#shared/view/components/CheckboxController";
import { Flex } from "#shared/view/components/Flex";
import { Stack } from "#shared/view/components/Stack";
import { Button } from "#shared/view/designSystem/Button";
import { SelectInputController } from "#shared/view/designSystem/inputs/SelectInputController";
import {
  getMaterialDensity,
  getMaterialDimensionsFieldsConfiguration,
  getMaterialTypesFromPartType,
  MaterialGradeType,
  MaterialTypeSchema,
  MaterialTypeType,
  PartTypeType,
  PricingModeSchema,
  round,
} from "shared";

export type RawMaterialFormData = {
  length: number;
  width: number;
  height: number;
  tubeThickness: number | null;
  price: number;
  createMaterialToCatalog: boolean;
  updateMaterialInCatalog: boolean;
  applyToAllSiblings: boolean;
  catalogMaterialType: MaterialTypeType | null;
};

type Props = {
  quoteRequestLine: QuoteRequestLineDto;
  close: () => void;
  cancel: () => void;
  materialGrade: MaterialGradeType;
  partType: PartTypeType | null;
  partSubType: string | null;
  overthickness: QuoteRequestLineOverthicknessDto;
  hasChildren: boolean;
};

export const RawMaterialLayoutForm = ({
  quoteRequestLine,
  close,
  cancel,
  materialGrade,
  partType,
  partSubType,
  overthickness,
  hasChildren,
}: Props) => {
  const analytics = useAnalytics();

  const existingMaterialLayout = useMemo(
    () => quoteRequestLine.layout?.materialLayout,
    [quoteRequestLine],
  );

  const { mutateAsync: createMaterialInCatalog } = useCreateMaterial();
  const { mutateAsync: updateMaterialInCatalog } = useUpdateMaterial();

  const { updateMaterialLayoutAsync, isPending: isUpdatePending } = useUpdateMaterialLayout({
    quoteRequestLineId: quoteRequestLine.id,
  });

  const { createMaterialLayoutAsync, isPending: isCreatePending } = useCreateMaterialLayout({
    layoutId: quoteRequestLine.layoutId,
    quoteRequestLineId: quoteRequestLine.id,
  });

  const isPending = isUpdatePending || isCreatePending;

  const allPossibleMaterialTypes = useMemo(() => {
    if (!partType) {
      return Object.values(MaterialTypeSchema.enum);
    }
    return getMaterialTypesFromPartType(partType);
  }, [partType]);

  const materialTypesOptions = useMemo(() => {
    return allPossibleMaterialTypes.map((materialType) => ({
      label: t(`qrl.materialTypes.${materialType}`),
      value: materialType,
    }));
  }, [allPossibleMaterialTypes]);

  const hasExistingCatalogMaterial = !!existingMaterialLayout?.materialId;

  const formDefaultValues = {
    length: existingMaterialLayout?.length,
    width: existingMaterialLayout?.width,
    height: existingMaterialLayout?.height,
    tubeThickness: existingMaterialLayout?.tubeThickness,
    price: existingMaterialLayout?.price ?? 0,
    createMaterialToCatalog: false,
    updateMaterialInCatalog: false,
    applyToAllSiblings: false,
    catalogMaterialType: allPossibleMaterialTypes[0],
  };

  const formMethods = useForm<RawMaterialFormData>({
    defaultValues: formDefaultValues,
  });

  const { setValue, handleSubmit, control } = formMethods;
  const createMaterialToCatalog = useWatch({ control, name: "createMaterialToCatalog" });
  const length = useWatch({ control, name: "length" });
  const width = useWatch({ control, name: "width" });
  const height = useWatch({ control, name: "height" });
  const tubeThickness = useWatch({ control, name: "tubeThickness" });

  const density = existingMaterialLayout?.density ?? getMaterialDensity({ materialGrade });
  const pricePartType = existingMaterialLayout?.type ?? partType;

  const doCreateMaterialToCatalog = async (data: RawMaterialFormData) => {
    if (!data.catalogMaterialType) {
      showErrorToast({ title: t("material.catalogMaterialTypeRequired") });

      return null;
    }

    const catalogPayload: CreateMaterialDto = {
      name: quoteRequestLine.materialName,
      type: data.catalogMaterialType,
      subType: partSubType,
      height: data.height,
      width: data.width,
      length: data.length,
      tubeThickness: data.tubeThickness ?? null,
      density: existingMaterialLayout?.density ?? getMaterialDensity({ materialGrade }),
      materialGrade,
      price: round(data.price),
    };
    return createMaterialInCatalog(catalogPayload);
  };

  const handleValidate = async (data: RawMaterialFormData) => {
    let createdMaterialId;
    if (data.createMaterialToCatalog && !hasExistingCatalogMaterial) {
      const createdMaterial = await doCreateMaterialToCatalog(data);
      if (!createdMaterial) {
        return;
      }
      createdMaterialId = createdMaterial.id;
    }

    if (data.updateMaterialInCatalog && hasExistingCatalogMaterial) {
      void updateMaterialInCatalog({
        id: existingMaterialLayout.materialId!,
        payload: {
          price: round(data.price),
          length: data.length,
          width: data.width,
          height: data.height,
          tubeThickness: data.tubeThickness,
        },
      });
    }

    if (!existingMaterialLayout) {
      if (!quoteRequestLine.layoutId || !data.length || !data.width || !data.height) {
        showErrorToast({ title: "Impossible de créer le matériau" });

        return;
      }

      const createPayload: CreateMaterialLayoutDto = {
        quoteRequestId: quoteRequestLine.quoteRequestId,
        price: round(data.price),
        tubeThickness: data.tubeThickness ?? undefined,
        density: getMaterialDensity({ materialGrade }),
        length: data.length,
        width: data.width,
        height: data.height,
        layoutId: quoteRequestLine.layoutId,
        quantity: 1,
        pricingMode: PricingModeSchema.enum.RAW,
        type: partType ?? undefined,
        materialId: createdMaterialId,
      };

      await createMaterialLayoutAsync({
        payload: createPayload,
      });
      analytics.capture("quote_request_line:material_layout_create", {
        quote_request_line_id: quoteRequestLine.id,
      });
    } else {
      await updateMaterialLayoutAsync({
        id: existingMaterialLayout.id,
        payload: {
          data: {
            ...existingMaterialLayout,
            length: data.length,
            width: data.width,
            height: data.height,
            tubeThickness: data.tubeThickness,
            price: round(data.price),
            materialId: getNewMaterialId(data, existingMaterialLayout, createdMaterialId),
          },
          applyToAllSiblings: data.applyToAllSiblings,
        },
      });
      analytics.capture("quote_request_line:material_layout_update", {
        quote_request_line_id: quoteRequestLine.id,
      });
    }

    formMethods.reset(formDefaultValues);

    close();
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(handleValidate)}>
        <Stack gap="sm">
          <PartHeader
            quoteRequestLine={quoteRequestLine}
            disablePartTypeChip
            hasChildren={hasChildren}
            isConsumable={false}
            thumbnailSize={{ width: 100, height: 100 }}
            fontSize={24}
            part={quoteRequestLine.part}
            showWeight
          />
          <RawMaterialDimensionsForm
            quoteRequestLineId={quoteRequestLine.id}
            setValue={setValue}
            overthickness={overthickness}
          />
          {pricePartType && (
            <MaterialRawPrice
              name="price"
              control={control}
              density={density}
              partType={pricePartType}
              geometry={{
                length,
                width,
                height,
                tubeThickness: tubeThickness ?? 0,
              }}
            />
          )}
          {materialTypesOptions.length > 0 && !hasExistingCatalogMaterial && (
            <CheckboxController
              name="createMaterialToCatalog"
              control={control}
              label={t("material.createMaterialToCatalog")}
            />
          )}
          {hasExistingCatalogMaterial && (
            <>
              <CheckboxController
                name="updateMaterialInCatalog"
                control={control}
                label={t("material.updateMaterialInCatalog")}
              />
              <CheckboxController
                name="applyToAllSiblings"
                control={control}
                label={t("material.applyToAllSiblings")}
              />
            </>
          )}
          {createMaterialToCatalog && materialTypesOptions.length > 1 && (
            <SelectInputController
              name="catalogMaterialType"
              label={t("material.catalogMaterialType")}
              control={control}
              data={materialTypesOptions}
            />
          )}
          <Flex gap="xs" justify="end">
            <Button variant="outline" onClick={cancel}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isPending} loading={isPending}>
              {t("common.validate")}
            </Button>
          </Flex>
        </Stack>
      </form>
    </FormProvider>
  );
};

const getNewMaterialId = (
  data: RawMaterialFormData,
  existingMaterialLayout: NonNullable<NonNullable<QuoteRequestLineDto["layout"]>["materialLayout"]>,
  createdMaterialId: string | undefined,
): string | null => {
  if (data.createMaterialToCatalog) {
    return createdMaterialId ?? null;
  }
  if (data.updateMaterialInCatalog) {
    return existingMaterialLayout.materialId;
  }

  const fields = getMaterialDimensionsFieldsConfiguration(existingMaterialLayout.type);

  const hasDimensionsChanged = fields.some((field) => {
    return data[field.field] !== existingMaterialLayout[field.field];
  });

  return hasDimensionsChanged ? null : existingMaterialLayout.materialId;
};
